import {
	AbstractJastVisitor,
	type AstNode,
	isOperandNode,
	type TerminalClause,
	COMPOUND_OPERATOR_OR,
	type CompoundClause,
	COMPOUND_OPERATOR_AND,
} from '@atlaskit/jql-ast';
import { fg } from '@atlassian/jira-feature-gating';

export type Result = CompoundClause | TerminalClause | undefined;

/**
 * Returns all AST terminal clauses that match the provided field's JQL term (case-insensitive)
 */
export class TextSearchInputClauseCollectingVisitor extends AbstractJastVisitor<Result> {
	protected textSearchInputJqlTerm: string;

	constructor(textSearchInputJqlTerm: string) {
		super();
		this.textSearchInputJqlTerm = textSearchInputJqlTerm;
	}

	visitCompoundClause = (compoundClause: CompoundClause): Result => {
		const operator = compoundClause.operator.value;

		if (operator === COMPOUND_OPERATOR_AND) {
			return compoundClause.clauses.reduce<Result>(
				(result, clause) => this.aggregateResult(clause.accept(this), result),
				undefined,
			);
		}

		if (operator === COMPOUND_OPERATOR_OR) {
			if (compoundClause.clauses.length !== 2 && !fg('too_many_text_search_input_clauses_fix')) {
				return;
			}

			for (const clause of compoundClause.clauses) {
				// Check if contains the text search input clause
				if (clause.accept(this)) {
					return compoundClause;
				}
			}
		}
	};

	visitTerminalClause = (terminalClause: TerminalClause): Result => {
		const fieldName = terminalClause.field.value.toLowerCase();

		if (fieldName === this.textSearchInputJqlTerm) {
			return terminalClause;
		}
	};

	protected shouldVisitNextChild(node: AstNode): boolean {
		// Skip visiting operand subtrees, which aren't needed for clause collection and can make a big impact on performance
		return !isOperandNode(node);
	}

	protected aggregateResult(aggregate: Result, nextResult: Result): Result {
		return aggregate ?? nextResult;
	}

	protected defaultResult(): Result {
		return undefined;
	}
}
